import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  bioField,
  displayNameField,
  emailField,
  loginField,
  supportedTimezones,
  type User,
} from '@/entities/viewer';
import { SUPPORTED_LOCALES } from '@/shared/i18n';
import { FormError, useFieldError, useServerErrors } from '@/shared/forms';
import { Button, Combobox, TextAreaField, TextField } from '@/shared/ui';

import { type ProfileValues, useUpdateProfile } from './api';
import styles from './AccountForms.module.css';

const schema = z.object({
  login: loginField,
  email: emailField,
  display_name: displayNameField,
  bio: bioField,
  locale: z.enum(SUPPORTED_LOCALES),
  timezone: z.string().min(1, 'REQUIRED'),
  first_day_of_week: z.number().int().min(0).max(6),
});
const FIELDS = [
  'login',
  'email',
  'display_name',
  'bio',
  'locale',
  'timezone',
  'first_day_of_week',
] as const;

export function ProfileForm({ user }: { user: User }) {
  const { t } = useTranslation();
  const fieldError = useFieldError();
  const update = useUpdateProfile();
  const [saved, setSaved] = useState(false);
  const { register, handleSubmit, formState, setError, reset, control, setValue } =
    useForm<ProfileValues>({
      resolver: zodResolver(schema),
      values: {
        login: user.login,
        email: user.email,
        display_name: user.display_name,
        bio: user.bio ?? '',
        locale: user.locale,
        timezone: user.timezone,
        first_day_of_week: user.first_day_of_week,
      },
    });
  const timezone = useWatch({ control, name: 'timezone' });
  const serverErrors = useServerErrors(setError, FIELDS, {
    LOGIN_TAKEN: 'login',
    EMAIL_TAKEN: 'email',
  });

  const onSubmit = handleSubmit((values) => {
    serverErrors.clear();
    setSaved(false);
    // PATCH semantics: send only what changed.
    const changed = Object.fromEntries(
      FIELDS.filter((field) => formState.dirtyFields[field]).map((field) => [field, values[field]]),
    );
    update.mutate(changed, {
      onSuccess: (updated) => {
        reset({ ...values, email: updated.email, bio: updated.bio ?? '' });
        setSaved(true);
      },
      onError: serverErrors.handle,
    });
  });

  return (
    <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
      <FormError error={serverErrors.formError} />
      <fieldset className={styles.group}>
        <legend>{t('account.about')}</legend>
        <TextField
          label={t('auth.login')}
          autoComplete="username"
          error={fieldError(formState.errors.login?.message)}
          {...register('login')}
        />
        <TextField
          label={t('auth.email')}
          type="email"
          autoComplete="email"
          error={fieldError(formState.errors.email?.message)}
          {...register('email')}
        />
        <TextField
          label={t('auth.displayName')}
          autoComplete="name"
          error={fieldError(formState.errors.display_name?.message)}
          {...register('display_name')}
        />
        <TextAreaField
          label={t('account.bio')}
          rows={3}
          error={fieldError(formState.errors.bio?.message)}
          {...register('bio')}
        />
      </fieldset>
      <fieldset className={styles.group}>
        <legend>{t('account.regional')}</legend>
        <label className={styles.select}>
          <span>{t('account.locale')}</span>
          <select {...register('locale')}>
            {SUPPORTED_LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {t(`locale.${locale}`)}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.select}>
          <span>{t('account.timezone')}</span>
          <Combobox
            label={t('account.timezone')}
            value={timezone}
            options={supportedTimezones(user.timezone).map((zone) => ({
              value: zone,
              label: zone,
            }))}
            limit={500}
            onChange={(zone) => {
              setValue('timezone', zone, { shouldDirty: true, shouldValidate: true });
            }}
          />
          <span id="timezone-hint" className={styles.hint}>
            {t('account.timezoneHint')}
          </span>
        </div>
        <label className={styles.select}>
          <span>{t('account.firstDayOfWeek')}</span>
          <select {...register('first_day_of_week', { valueAsNumber: true })}>
            {[0, 1, 2, 3, 4, 5, 6].map((day) => (
              <option key={day} value={day}>
                {t(`calendar.weekdays.${day}` as 'calendar.weekdays.0')}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={update.isPending || !formState.isDirty}>
          {t('common.save')}
        </Button>
        {saved && (
          <span className={styles.success} role="status">
            {t('account.saved')}
          </span>
        )}
      </div>
    </form>
  );
}
